import {
    compose,
    getOr,
    get,
    map,
    zipAll,
    mergeAll,
    identity,
    take,
    max,
    flatten,
    omit,
    values,
    memoize,
    range,
    mapValues,
} from 'lodash/fp';
import { createSelector } from 'reselect';
import { getProp } from '@mews/fp';
import { createAsyncMetaSelectors, DateTime, DateTimeService } from '@mews/framework';
import { TimeUnit } from '@mews/enums';
import { ResourceCategoryType } from '@mews-commander/legacy-data-structures';
import { BreakpointRangeValue } from '@optimus-web/core';
import { BreakpointRange } from '@mews-commander/ui';
import { getContainer } from '@mews-commander/legacy-app-instance';
import { toPercents, getOrEmptyArray, getOrEmptyObject, dateTimeToUtc } from '@mews-commander/utils';
import { META_STATE_SUCCEEDED } from '@mews-commander/legacy-entities/src/constants';
import { getServiceTimeUnit } from '@mews-commander/legacy-entities/src/ducks/services';
import parentSelector from '../selector';
import { getResourceCategoryTypeOccupancyKey } from './utils';
import { MODULE_NAME } from './constants';

const baseSelector = createSelector(parentSelector, (state) => state[MODULE_NAME]);

const defaultOccupancyData = { ...META_STATE_SUCCEEDED };
const getOccupancyState = (state: $TSFixMe, { start, end, selectedServiceId }: $TSFixMe) => {
    const startUtc = dateTimeToUtc(start);
    const endUtc = dateTimeToUtc(end);
    return getOr(defaultOccupancyData, [selectedServiceId, startUtc, endUtc], baseSelector(state));
};
// @ts-expect-error $TSFixMeError: No overload matches this call.
const getOccupancyRawData = compose(getOrEmptyObject('data'), getOccupancyState);

const getOccupancyMeta = compose(get('meta'), getOccupancyState);

const maxColumnCount = (currentRange: $TSFixMe) => (currentRange === BreakpointRange.Phone ? 3 : 7);

const getDataFilter = (breakpointRange: $TSFixMe) => take(maxColumnCount(breakpointRange));

const diffTimeUnitRange = (start: $TSFixMe, end: $TSFixMe, timeUnit: $TSFixMe) =>
    // @ts-expect-error $TSFixMeError: Element implicitly has an 'any' type because expression of type 'any' can't be used to index type 'typeof TimeUnit'.
    range(0, DateTimeService.diff(TimeUnit[timeUnit])(end, start) + 1);

const memoizeWithResolver = (memoize as $TSFixMe).convert({ fixed: false });

const daysRange = (start: $TSFixMe, end: $TSFixMe, timeUnit: $TSFixMe) =>
    // @ts-expect-error $TSFixMeError: Element implicitly has an 'any' type because expression of type 'any' can't be used to index type 'typeof TimeUnit'.
    map((item) => DateTimeService.add(TimeUnit[timeUnit])(start, item))(diffTimeUnitRange(start, end, timeUnit));

export const createMemoizedDaysRange = <S>(selector: S): S =>
    memoizeWithResolver(
        selector,
        (start: $TSFixMe, end: $TSFixMe, timeUnit: $TSFixMe) =>
            `${start.value}.${start.timezone}.${end.value}.${end.timezone}.${timeUnit}`
    );

const memoizeTimeUnitRange = createMemoizedDaysRange(daysRange);

const timeUnitRangeSelector = (state: $TSFixMe, { selectedServiceId, start, end }: $TSFixMe) => {
    const timeUnit = getServiceTimeUnit(state, { id: selectedServiceId });

    return memoizeTimeUnitRange(start, end, timeUnit);
};

const mapResourceCategoryTypeOccupancy = (occupancy: $TSFixMe) =>
    map(
        (item) => ({
            [getResourceCategoryTypeOccupancyKey(ResourceCategoryType.getByName(occupancy.resourceCategoryType).value)]:
                toPercents(item.value, 1),
        }),
        occupancy.occupancies
    );

const createOccupancyDataSelector = (getFilter = () => identity) =>
    createSelector(
        timeUnitRangeSelector,
        getOccupancyRawData,
        getProp<BreakpointRangeValue>()('breakpointRange'),
        (timeUnits, data, breakpointRange) => {
            const container = getContainer();
            const { appLocalizationComponent } = container();
            const rawData = [
                timeUnits,
                // @ts-expect-error $TSFixMeError: No overload matches this call.
                getOrEmptyArray('totalOccupancies', data),
                // @ts-expect-error $TSFixMeError: No overload matches this call.
                getOrEmptyArray('arrivals', data),
                // @ts-expect-error $TSFixMeError: No overload matches this call.
                getOrEmptyArray('departures', data),
                // @ts-expect-error $TSFixMeError: No overload matches this call.
                ...map(mapResourceCategoryTypeOccupancy, getOrEmptyArray('resourceCategoryTypeOccupancies', data)),
            ];
            // @ts-expect-error $TSFixMeError: Expected 0 arguments, but got 1.
            const filter = getFilter(breakpointRange);
            return compose(
                map(([date, total, arrivals, departures, ...byResourceCategoryType]) =>
                    mergeAll([
                        {
                            date: appLocalizationComponent.localizeDate(date as DateTime),
                            total: toPercents(getOr(0, 'value', total)),
                            arrivals,
                            departures,
                        },
                        ...byResourceCategoryType,
                    ])
                ),
                zipAll,
                map(filter)
            )(rawData);
        }
    );

// @ts-expect-error $TSFixMeError: Argument of type '(breakpointRange: $TSFixMe) => _.LodashTake1x1' is not assignable to parameter of type '() => _.Lodash...
export const getOccupancyData = createOccupancyDataSelector(getDataFilter);
export const getAllOccupancyData = createOccupancyDataSelector();

// @ts-expect-error $TSFixMeError: Argument of type '(a1: any, a2: any) => { hasFailed: boolean; isFetching: boolean; } | undefined' is not assignable to p...
export const [isFetching, hasFailed] = createAsyncMetaSelectors(getOccupancyMeta);

const getOccupancyValues = compose(values, omit(['date', 'arrivals', 'departures']));

export const getMaxOccupancyAxisValue = compose(max, flatten, map(getOccupancyValues), getOccupancyData);

export const getOccupancyTableData = createSelector(
    timeUnitRangeSelector,
    getOccupancyRawData,
    getProp<BreakpointRangeValue>()('breakpointRange'),
    (timeUnits, data, breakpointRange) => {
        const takeTimeUnits = getDataFilter(breakpointRange);
        return {
            timeUnits: takeTimeUnits(timeUnits),
            ...mapValues(takeTimeUnits, data),
        };
    }
);
